'use client';

import React from 'react';
import usePlacesAutocomplete, { getGeocode, getZipCode, getDetails } from 'use-places-autocomplete';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { cn } from '@/lib/utils';

// I'll implement a simple click outside or just use the onBlur with timeout.

interface AddressAutocompleteProps {
  onAddressSelect: (address: {
    name: string;
    line1: string;
    line2: string;
    city: string;
    postcode: string;
  }) => void;
  onIncompleteAddress?: (address: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    postcode?: string;
    reason?: 'missing_line1' | 'missing_components';
  }) => void;
  defaultValue?: string;
  label?: string;
}

// Helper to parse Google address components into structured address
function parseAddressComponents(components: google.maps.GeocoderAddressComponent[]) {
  const componentMap: Record<string, string> = {};

  components.forEach((component) => {
    const types = component.types;
    if (types.includes('street_number')) componentMap.streetNumber = component.long_name;
    if (types.includes('route')) componentMap.route = component.long_name;
    if (types.includes('premise') || types.includes('establishment') || types.includes('point_of_interest')) {
      componentMap.premise = component.long_name;
    }
    if (types.includes('subpremise')) componentMap.subpremise = component.long_name;
    if (types.includes('postal_town') || types.includes('locality')) componentMap.city = component.long_name;
    if (types.includes('postal_code')) componentMap.postcode = component.long_name;
  });

  return componentMap;
}

// Helper to extract building/business name from search description
function extractName(searchAddress: string, streetAddress: string, premise?: string): string {
  const firstPart = searchAddress.split(',')[0]?.trim();

  // If first part is not just a street number + route, it's likely a business/building name
  if (firstPart && firstPart !== streetAddress && !/^\d+\s/.test(firstPart)) {
    return firstPart;
  }

  return premise || '';
}

export const AddressAutocomplete = ({ onAddressSelect, onIncompleteAddress }: AddressAutocompleteProps) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'gb' },
    },
    debounce: 300,
  });

  const ref = React.useRef<HTMLDivElement>(null);

  // Handle click outside to close suggestions
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        clearSuggestions();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearSuggestions]);

  const handleSelect = async (address: string) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const components = parseAddressComponents(results[0].address_components);

      const line1 = `${components.streetNumber || ''} ${components.route || ''}`.trim();

      // If we couldn't build a proper line1, treat as incomplete
      if (!line1) {
        onIncompleteAddress?.({
          name: extractName(address, line1, components.premise),
          line1: '',
          line2: components.subpremise || '',
          city: components.city || '',
          postcode: components.postcode || '',
          reason: 'missing_line1',
        });
        return;
      }

      onAddressSelect({
        name: extractName(address, line1, components.premise),
        line1,
        line2: components.subpremise || '',
        city: components.city || '',
        postcode: components.postcode || '',
      });
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  // Filter to only show complete addresses (with street and secondary details)
  const isCompleteAddress = ({ structured_formatting }: any) => {
    const main = structured_formatting?.main_text || '';
    const secondary = structured_formatting?.secondary_text || '';
    return Boolean(main && secondary);
  };

  return (
    <div className="relative" ref={ref}>
      <Input
        id="address-lookup"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={!ready}
        placeholder="Start typing address..."
        className="mt-1"
        autoComplete="off"
      />
      {status === 'OK' && (
        <ul className="absolute w-full bg-white border border-border rounded-md shadow-lg mt-1 max-h-[240px] overflow-y-auto z-[99999]">
          {data.filter(isCompleteAddress).map(({ place_id, description }) => (
            <li
              key={place_id}
              onClick={() => handleSelect(description)}
              className="px-4 py-3 bg-white hover:bg-gray-100 cursor-pointer text-base border-b border-gray-100 last:border-b-0"
            >
              {description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

